import './capacity-provider.container.scss';
import './underwriting.overview.requested-policy.scss';

import { Box, Stack } from '@mui/material';
import {
  FormContainerWithHeader,
  FormNumberInputAdornmentField,
  FormSearchableSelectField,
  NewDateField,
  NewFromSelectField,
  ProgramFormSelectField,
} from '../../../../presentation';
import {
  PRODUCT_TYPE_OPTIONS,
  getFieldProps,
  toTitleCase,
} from '@converge/data';
import { SubmissionType, useGetEntities } from '@converge/graphql';
import { addYear, checkPositiveNumber } from '@converge/utils';

import { CapacityProviderContainer } from './capacity-provider.container';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import { useState } from 'react';

interface IQuotaShare {
  uuid: string | undefined;
  name: string | null | undefined;
  quotaShare: number;
}
interface IProps {
  isReadonly: boolean;
  isQuoted: boolean;
  isPartnered: boolean;
  QuotaShare: IQuotaShare[];
  submissionUuid: string;
}

export const UnderwritingOverviewRequestedPolicySection = ({
  isReadonly,
  QuotaShare,
  submissionUuid,
}: IProps) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const submissionTypes = Object.entries(SubmissionType).map(
    ([value, label]) => ({
      value: value,
      label: toTitleCase(label),
    })
  );
  const { entities: partners = [] } = useGetEntities({
    isProgramPartner: true,
  });

  const PARTNER_OPTIONS = [
    { value: 'null', label: 'No Partner' },
    ...partners.map((partner) => ({
      value: partner?.uuid ?? '',
      label: partner?.name ?? 'N/A',
    })),
  ];
  const effectiveDate = watch('effectiveDate');
  const programLabel = watch('program.label');

  // Disable partner field if Digital program is selected
  const isPartnerDisabled = programLabel === 'Digital';

  return (
    <FormContainerWithHeader title={t('Policy')}>
      <Stack className="capacity-provider-row">
        <Box className="capacity-provider-container">
          <CapacityProviderContainer
            isReadonly={isReadonly}
            QuotaShare={QuotaShare}
            submissionUuid={submissionUuid}
          />
        </Box>
        <Box className="program-container">
          <ProgramFormSelectField
            {...getFieldProps('program.value', isReadonly, t('PROGRAM'))}
          />
        </Box>
      </Stack>

      <FormSearchableSelectField
        minDropdownWidth="530px"
        maxDropdownWidth="750px"
        {...getFieldProps(
          'partner.value',
          isReadonly || isPartnerDisabled,
          t(
            `underwriting.detail.content.sections.requestedPolicy.fields.partner`,
            'CONVERGECONNECT™ PARTNER'
          )
        )}
        options={PARTNER_OPTIONS}
        placeholder="Select Partner"
        onChangeOption={(option) => {
          setValue('partner.value', option?.value || 'null', {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />

      <FormSearchableSelectField
        minDropdownWidth="530px"
        maxDropdownWidth="750px"
        {...getFieldProps(
          'product.value',
          isReadonly,
          t(
            `underwriting.detail.content.sections.requestedPolicy.fields.product`,
            'PRODUCT'
          )
        )}
        options={PRODUCT_TYPE_OPTIONS}
        required
        placeholder="Select Product"
        onChangeOption={(option) => {
          setValue('product.value', option?.value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />
      <NewFromSelectField
        {...getFieldProps('type', isReadonly, t('POLICY TYPE'))}
        required
        options={submissionTypes}
        placeholder="Select Policy Type"
      />

      <NewDateField
        {...getFieldProps('createdDate', isReadonly, t('SUBMISSION DATE'))}
        placeholder="Select Effective Date"
      />

      <NewDateField
        {...getFieldProps('effectiveDate', isReadonly, t('EFFECTIVE DATE'))}
        onDateChange={(date) => {
          setValue('expirationDate', addYear(moment(date)), {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
        placeholder="Select Expiration Date"
      />
      <NewDateField
        {...getFieldProps('expirationDate', isReadonly, t('EXPIRATION DATE'))}
        minDate={effectiveDate}
      />
      <FormNumberInputAdornmentField
        adornmentText={'$'}
        thousandSeparator={','}
        {...getFieldProps('aggregateLimit', isReadonly, t('REQUESTED LIMIT'))}
        isAllowed={(values) => checkPositiveNumber(values)}
        placeholder="0"
      />
      <FormNumberInputAdornmentField
        adornmentText={'$'}
        thousandSeparator={','}
        {...getFieldProps('retention', isReadonly, t('REQUESTED RETENTION'))}
        isAllowed={(values) => checkPositiveNumber(values)}
        placeholder="0"
      />
      <FormNumberInputAdornmentField
        adornmentText={'$'}
        {...getFieldProps(
          'policyFee',
          isReadonly,

          t('POLICY FEE')
        )}
        placeholder="0"
        decimalScale={2}
        isAllowed={(values) => checkPositiveNumber(values)}
      />
    </FormContainerWithHeader>
  );
};
